import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import type { PublicInterviewPage } from '@/types';

const dayTitle = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

const fullLabel = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

const dayKey = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function PublicInterviewBooking() {
  const { token } = useParams();
  const [data, setData] = useState<PublicInterviewPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [justBooked, setJustBooked] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  const tzName = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return '';
    }
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.publicGet<PublicInterviewPage>(`/interview/${token}`);
      setData(res);
      setError('');
    } catch (e) {
      setError((e as Error).message || 'Ссылка недействительна');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleBook = async () => {
    if (!selected || !token) return;
    setBusy(true);
    try {
      await api.publicPost(`/interview/${token}/book`, { slot_id: selected, note: note.trim() || null });
      setJustBooked(true);
      setRescheduling(false);
      setSelected(null);
      setNote('');
      await load();
    } catch (e) {
      const msg = (e as Error).message || 'Не удалось забронировать';
      setError(msg);
      await load();
    }
    setBusy(false);
  };

  const handleCancel = async () => {
    if (!token) return;
    if (!confirm('Отменить запись на собеседование?')) return;
    setBusy(true);
    try {
      await api.publicPost(`/interview/${token}/cancel`, {});
      setJustBooked(false);
      await load();
    } catch (e) {
      setError((e as Error).message || 'Не удалось отменить');
    }
    setBusy(false);
  };

  const byDay = useMemo(() => {
    const map = new Map<string, PublicInterviewPage['slots']>();
    for (const slot of data?.slots || []) {
      const key = dayKey(slot.starts_at);
      const list = map.get(key) || [];
      list.push(slot);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
            <i className="ri-link-unlink text-2xl text-red-500"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
            Ссылка недействительна
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed">
            {error || 'Возможно, срок ссылки истёк. Напишите рекрутеру — он пришлёт новую.'}
          </p>
        </div>
      </div>
    );
  }

  const booked = data.booked;
  const showPicker = !booked || rescheduling;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Шапка */}
      <div className="bg-white border-b border-background-200">
        <div className="max-w-[880px] mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-5">
            {data.organization_logo_url ? (
              <img
                src={data.organization_logo_url}
                alt={data.company_name}
                className="w-10 h-10 rounded-lg object-contain bg-background-50"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center">
                <span className="text-white font-heading font-bold">
                  {data.company_name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-foreground-800">{data.company_name}</p>
              <p className="text-xs text-foreground-400">{data.vacancy_title}</p>
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
            {data.candidate_name ? `${data.candidate_name}, выберите время собеседования` : 'Выберите время собеседования'}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            Длительность встречи — {data.duration_min} минут.
            {tzName && ` Время указано в вашем часовом поясе (${tzName}).`}
          </p>
        </div>
      </div>

      <div className="max-w-[880px] mx-auto px-6 py-8 space-y-6">
        {/* Уже забронировано */}
        {booked && (
          <div className={`rounded-xl border p-6 ${justBooked ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-background-200'}`}>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <i className="ri-check-line text-xl text-emerald-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground-900">
                  {justBooked ? 'Время подтверждено' : 'Ваша запись'}
                </p>
                <p className="text-base font-heading font-bold text-foreground-950 mt-1 capitalize">
                  {fullLabel(booked.starts_at)}
                </p>
                <p className="text-xs text-foreground-500 mt-1">
                  {booked.duration_min} минут
                  {booked.location ? ` · ${booked.location}` : ''}
                </p>
                <p className="text-xs text-foreground-500 mt-3">
                  Подтверждение с файлом календаря отправлено на вашу почту.
                </p>
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  {!rescheduling && (
                    <button
                      onClick={() => { setRescheduling(true); setJustBooked(false); }}
                      className="px-4 py-2 rounded-lg bg-white border border-background-200 text-sm text-foreground-700 hover:border-primary-400 transition-colors cursor-pointer"
                    >
                      <i className="ri-calendar-event-line mr-1.5"></i>
                      Перенести
                    </button>
                  )}
                  <button
                    onClick={handleCancel}
                    disabled={busy}
                    className="px-4 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    Отменить запись
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && booked && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
        )}

        {/* Выбор окна */}
        {showPicker && (
          <>
            {error && !booked && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
            )}

            {byDay.length === 0 ? (
              <div className="bg-white rounded-xl border border-background-200 p-8 text-center">
                <p className="text-sm text-foreground-600">
                  Свободных окон сейчас нет. Мы добавим новые слоты и сообщим вам — или напишите рекрутеру.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-200">
                {rescheduling && (
                  <div className="p-4 bg-amber-50 flex items-center justify-between gap-3">
                    <p className="text-sm text-amber-900">Выберите новое время — прежнее освободится.</p>
                    <button
                      onClick={() => setRescheduling(false)}
                      className="text-xs text-amber-800 hover:underline cursor-pointer"
                    >
                      Отмена
                    </button>
                  </div>
                )}
                {byDay.map(([day, slots]) => (
                  <div key={day} className="p-5">
                    <p className="text-sm font-medium text-foreground-800 mb-3 capitalize">{dayTitle(day)}</p>
                    <div className="flex flex-wrap gap-2">
                      {slots.map((slot) => {
                        const active = selected === slot.id;
                        return (
                          <button
                            key={slot.id}
                            onClick={() => setSelected(slot.id)}
                            className={`px-4 py-2.5 rounded-lg border text-sm font-medium tabular-nums transition-colors cursor-pointer ${
                              active
                                ? 'bg-primary-500 border-primary-500 text-white'
                                : 'bg-background-50 border-background-200 text-foreground-700 hover:border-primary-400'
                            }`}
                          >
                            {timeLabel(slot.starts_at)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selected && (
              <div className="bg-white rounded-xl border border-background-200 p-6 space-y-4">
                <div>
                  <p className="text-xs font-medium text-foreground-500 mb-1">Выбранное время</p>
                  <p className="text-base font-heading font-bold text-foreground-950 capitalize">
                    {fullLabel(data.slots.find((s) => s.id === selected)?.starts_at || '')}
                  </p>
                </div>
                <label className="block">
                  <span className="text-xs font-medium text-foreground-500 mb-1.5 block">
                    Комментарий для рекрутера (необязательно)
                  </span>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="Например: удобнее по видеосвязи"
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                </label>
                <button
                  onClick={handleBook}
                  disabled={busy}
                  className="w-full md:w-auto px-6 py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-calendar-check-line'}></i>
                  {rescheduling ? 'Перенести на это время' : 'Подтвердить время'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
